import { useState, ReactElement, useRef, useEffect } from "react";
import {
  Modal,
  ModalSize,
  Button,
} from "@screencloud/screencloud-ui-components";
import { useAutofocus } from "../../hooks/useAutofocus";
import "./StreamNamingModal.scss";

interface StreamNamingModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkStreamExists: (streamName: string) => boolean;
  onCreateClick: (streamName: string) => void;
  kind: "CREATE" | "RENAME";
  existingName?: string | null;
}

const isValid = (value: string): boolean => {
  if (!value) {
    return false;
  }
  return /^[a-zA-Z0-9 \u00C0-\u00FF]*$/.test(value);
};

const DEFAULT_LABELS = {
  TITLE: "Set up a Streaming Channel",
  DESCRIPTION:
    "A Streaming Channel is like your own dedicated TV Channel for live, episodic or related content. Once set up you can broadcast live video to it any time you need.",
  PLACEHOLDER: "Give it a name or subject",
  CONFIRM_BUTTON: "Continue",
};

const RENAME_LABELS = {
  TITLE: "Rename Streaming Channel",
  DESCRIPTION: "Update your channel name so it is more meaningful",
  PLACEHOLDER: "Give it a name or subject",
  CONFIRM_BUTTON: "Rename",
};

const LABELS = {
  CREATE: DEFAULT_LABELS,
  RENAME: RENAME_LABELS,
};

const NAMING_ERRORS = {
  IN_USE:
    "The stream name you have entered is already in use in your organization. Please try again with a different name",
  INVALID:
    "The stream name you have entered is not valid. Please make sure you only include characters, numbers or spaces.",
};

const StreamNamingModal = (
  props: StreamNamingModalProps
): ReactElement<StreamNamingModalProps> => {
  const { isOpen, onClose, checkStreamExists, onCreateClick, kind } = props;
  const [streamName, setStreamName] = useState("");
  const [isStreamNameValid, setIsStreamNameValid] = useState(true);
  const [streamAlreadyExists, setStreamAlreadyExists] = useState(false);
  const streamNameInputRef = useRef<HTMLInputElement>(null);
  useAutofocus(streamNameInputRef, isOpen);

  const currentLabels = LABELS[kind];

  const { existingName } = props;

  useEffect(() => {
    setStreamName("");
    if (existingName !== null && existingName !== undefined) {
      setStreamName(existingName);
    }
  }, [isOpen, existingName]);

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        setIsStreamNameValid(true);
        setStreamName("");
        onClose();
      }}
      size={ModalSize.MEDIUM}
      className="stream-naming-modal"
    >
      <div className="stream-naming-content">
        <span className="header">{currentLabels.TITLE}</span>
        <span className="description">{currentLabels.DESCRIPTION}</span>
        <input
          ref={streamNameInputRef}
          className={
            isStreamNameValid && !streamAlreadyExists
              ? "stream-name-input"
              : "stream-name-input invalid"
          }
          type="text"
          value={streamName}
          onChange={(e) => {
            setStreamName(e.target.value);
            setIsStreamNameValid(isValid(e.target.value));
            setStreamAlreadyExists(checkStreamExists(e.target.value));
          }}
          placeholder={currentLabels.PLACEHOLDER}
          autoFocus
        />
        {!isStreamNameValid && (
          <span className="error-message">{NAMING_ERRORS.INVALID}</span>
        )}
        {streamAlreadyExists && (
          <span className="error-message">{NAMING_ERRORS.IN_USE}</span>
        )}
        <Button
          className="continue-button"
          primary
          disabled={!isStreamNameValid || !streamName || streamAlreadyExists}
          onClick={() => {
            if (!checkStreamExists(streamName)) {
              onCreateClick(streamName);
            } else {
              setStreamAlreadyExists(true);
            }
          }}
        >
          {currentLabels.CONFIRM_BUTTON}
        </Button>
      </div>
    </Modal>
  );
};

export default StreamNamingModal;
